import { useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import SessionNotesEditor from "@/components/admin/SessionNotesEditor";
import {
  Loader2,
  X,
  Save,
  Check,
  CalendarClock,
  Tag,
  Palette,
  Mail,
  Phone,
  MapPin,
  Clock,
  Trash2,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

// Curated brand-palette swatches admins can apply to bookings.
export const BOOKING_COLOR_SWATCHES = [
  { hex: "", name: "Default" },
  { hex: "#0E3B4A", name: "Navy" },
  { hex: "#4A7C7C", name: "Teal" },
  { hex: "#7A8F6B", name: "Sage" },
  { hex: "#D2A857", name: "Honey" },
  { hex: "#B85C45", name: "Terracotta" },
  { hex: "#6F4A6F", name: "Plum" },
  { hex: "#5C6B73", name: "Slate" },
];

/**
 * Helper used by Week + Month cells to compute a friendly cell color
 * based on the booking's status/kind/color preference.
 */
export function bookingCellStyle(b) {
  const isBlock = b.kind === "block";
  if (isBlock) {
    if (b.color) {
      // Coloured block: solid fill + dashed border to keep the "blocked" visual cue
      return {
        className: "border border-dashed text-cream",
        style: { backgroundColor: b.color, borderColor: "rgba(255,255,255,0.35)" },
      };
    }
    return {
      className: "bg-cream border border-dashed border-sand text-ink-soft",
    };
  }
  if (b.status === "completed") {
    return { className: "bg-navy-light/15 text-ink-soft" };
  }
  if (b.color) {
    return {
      className: "text-cream",
      style: { backgroundColor: b.color },
    };
  }
  return { className: "bg-navy text-cream" };
}

export default function BookingDetailModal({ booking, openNotes = false, onClose, onUpdated }) {
  const isBlock = booking.kind === "block";
  const [color, setColor] = useState(booking.color || "");
  const [customLabel, setCustomLabel] = useState(booking.custom_label || "");
  const [notes, setNotes] = useState(booking.notes || "");
  const [saving, setSaving] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  // Notes editor visible when "Mark complete" was clicked OR booking is already completed OR
  // banner navigated us here (openNotes prop).
  const [showNotes, setShowNotes] = useState(openNotes || booking.status === "completed");
  const [reschedule, setReschedule] = useState({
    date: booking.date,
    start_time: booking.start_time,
    duration_min: booking.duration_min || 60,
  });

  const dirty =
    (color || "") !== (booking.color || "") ||
    customLabel !== (booking.custom_label || "") ||
    notes !== (booking.notes || "");

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/bookings/${booking.id}`, {
        color,
        custom_label: customLabel,
        notes,
      });
      toast.success("Saved");
      onUpdated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (status) => {
    if (status === "cancelled" && !window.confirm("Cancel this booking?")) return;
    setSaving(true);
    try {
      await api.patch(`/admin/bookings/${booking.id}`, { status });
      toast.success(status === "cancelled" ? "Cancelled" : `Marked ${status}`);
      onUpdated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const submitReschedule = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/bookings/${booking.id}/reschedule`, {
        date: reschedule.date,
        start_time: reschedule.start_time,
        duration_min: parseInt(reschedule.duration_min, 10),
      });
      toast.success("Booking rescheduled");
      onUpdated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const cancelSeries = async () => {
    const freq = booking.series_frequency || "recurring";
    if (!window.confirm(`Cancel this ${freq} ${isBlock ? "block" : "session"} AND all future occurrences in the series? Past sessions will stay intact.`)) return;
    setSaving(true);
    try {
      const r = await api.post(`/admin/bookings/${booking.id}/cancel-series`);
      toast.success(`Cancelled ${r.data?.cancelled || 0} ${isBlock ? "blocks" : "sessions"} in the series`);
      onUpdated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-50 flex items-start justify-center p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="booking-detail-modal"
        className="bg-cream-soft border border-sand/70 rounded-3xl p-7 w-full max-w-xl mt-12 mb-12 shadow-2xl"
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-ink-soft">
              {isBlock ? "Block" : "Booking"} · {booking.id.slice(0, 8).toUpperCase()}
            </div>
            <h3 className="font-serif text-2xl text-ink leading-tight mt-1">
              {customLabel || (isBlock ? booking.label || "Blocked" : booking.client_name)}
            </h3>
            <div className="text-sm text-ink-muted mt-1 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <CalendarClock size={13} />
                {format(new Date(booking.date), "EEE d MMM yyyy")} · {booking.start_time}–{booking.end_time}
              </span>
              <span>·</span>
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={13} /> {booking.location_name}
              </span>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink">
            <X size={20} />
          </button>
        </div>

        {booking.series_id && booking.status !== "cancelled" && (
          <div className="mb-4 flex items-center gap-2 text-xs bg-navy/5 border border-navy/15 rounded-xl px-3 py-2 text-navy" data-testid="series-banner">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-navy" />
              Part of a {booking.series_frequency || "recurring"} series
            </span>
          </div>
        )}

        {!isBlock && (
          <div className="bg-cream rounded-2xl border border-sand/60 p-4 mb-4 text-sm">
            <div className="flex items-center gap-2 text-ink mb-1">
              <span className="font-medium">{booking.client_name}</span>
              <span className={`text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full ${
                booking.status === "completed" ? "bg-navy-light/20 text-navy-deep" :
                booking.status === "cancelled" ? "bg-red-100 text-red-800" :
                "bg-navy/10 text-navy"
              }`}>{booking.status}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-1 text-ink-muted text-xs">
              <a href={`mailto:${booking.client_email}`} className="flex items-center gap-1.5 hover:text-navy"><Mail size={11} />{booking.client_email}</a>
              {booking.client_phone && <span className="flex items-center gap-1.5"><Phone size={11} />{booking.client_phone}</span>}
              <span className="flex items-center gap-1.5"><Clock size={11} />{booking.service_name} · {booking.duration_min || 60} min</span>
              {booking.buffer_min > 0 && <span className="text-ink-soft">+{booking.buffer_min}m buffer</span>}
              {booking.referral_source && (
                <span className="sm:col-span-2 text-ink-soft" data-testid="booking-referral-source">
                  Heard via: <span className="text-ink">{booking.referral_source}</span>
                </span>
              )}
            </div>
          </div>
        )}

        {/* Color swatches */}
        <div className="mb-4">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-2">
            <Palette size={12} /> Calendar colour
          </div>
          <div className="flex flex-wrap gap-2" data-testid="color-picker">
            {BOOKING_COLOR_SWATCHES.map((s) => {
              const active = (color || "") === s.hex;
              return (
                <button
                  key={s.hex || "default"}
                  type="button"
                  onClick={() => setColor(s.hex)}
                  data-testid={`color-${s.name.toLowerCase()}`}
                  title={s.name}
                  className={`h-8 w-8 rounded-full border-2 transition-all flex items-center justify-center ${
                    active ? "border-ink scale-110" : "border-sand/60 hover:border-ink/60"
                  }`}
                  style={s.hex ? { backgroundColor: s.hex } : { backgroundColor: "#1A3636" }}
                >
                  {active && <Check size={14} className="text-cream" strokeWidth={3} />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Custom label */}
        <div className="mb-4">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-2">
            <Tag size={12} /> Calendar label (replaces the default name shown in the week/month view)
          </div>
          <input
            className="w-full bg-cream border border-sand/80 rounded-xl px-3 py-2.5 text-ink focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy text-sm"
            value={customLabel}
            onChange={(e) => setCustomLabel(e.target.value)}
            placeholder={isBlock ? booking.label || "Blocked" : (booking.client_name || "")}
            data-testid="custom-label-input"
            maxLength={80}
          />
        </div>

        {/* Notes */}
        {!isBlock && (
          <div className="mb-4">
            <div className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-2">Notes</div>
            <textarea
              className="w-full bg-cream border border-sand/80 rounded-xl px-3 py-2.5 text-ink focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy text-sm font-sans resize-y"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="notes-input"
            />
          </div>
        )}

        {/* Reschedule (available for both sessions AND blocks) */}
        {booking.status !== "cancelled" && (
          <div className="mb-4">
            <button
              type="button"
              onClick={() => setShowReschedule((v) => !v)}
              data-testid="toggle-reschedule"
              className="text-xs text-navy hover:underline"
            >
              {showReschedule ? "Hide edit time" : (isBlock ? "Edit date / time / duration" : "Reschedule this booking")}
            </button>
            {showReschedule && (
              <div className="mt-3 grid sm:grid-cols-3 gap-2 p-3 bg-cream border border-sand/60 rounded-xl">
                <input type="date" className="bg-cream-soft border border-sand rounded-lg px-3 py-2 text-sm" value={reschedule.date} onChange={(e) => setReschedule({ ...reschedule, date: e.target.value })} />
                <input type="time" step="900" className="bg-cream-soft border border-sand rounded-lg px-3 py-2 text-sm" value={reschedule.start_time} onChange={(e) => setReschedule({ ...reschedule, start_time: e.target.value })} />
                <input type="number" step="15" min="15" max="1440" className="bg-cream-soft border border-sand rounded-lg px-3 py-2 text-sm" value={reschedule.duration_min} onChange={(e) => setReschedule({ ...reschedule, duration_min: e.target.value })} />
                {isBlock && booking.block_group_id && (
                  <div className="sm:col-span-3 text-[11px] text-ink-soft italic">
                    Grouped block — the change applies to all locations in this group.
                  </div>
                )}
                <button
                  type="button"
                  onClick={submitReschedule}
                  disabled={saving}
                  data-testid="confirm-reschedule"
                  className="sm:col-span-3 inline-flex items-center justify-center gap-2 rounded-full bg-navy-light text-cream px-5 py-2 text-sm hover:bg-navy transition-colors disabled:opacity-60"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />} {isBlock ? "Save changes" : "Confirm reschedule"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Session notes editor (sessions only, opens on Mark complete OR when booking is already completed) */}
        {!isBlock && showNotes && (
          <SessionNotesEditor
            booking={booking}
            onAttached={onUpdated}
            onCompleted={onUpdated}
          />
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-sand/60">
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty}
            data-testid="booking-save"
            className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-medium hover:bg-navy-deep transition-colors disabled:opacity-40"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
          {!isBlock && booking.status !== "completed" && (
            <button
              type="button"
              onClick={() => setShowNotes(true)}
              disabled={saving}
              data-testid="open-notes-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-navy-light text-cream px-4 py-2.5 text-xs font-medium hover:bg-navy transition-colors"
            >
              <Check size={12} /> Mark complete &amp; add notes
            </button>
          )}
          {booking.status !== "cancelled" && (
            <button type="button" onClick={() => setStatus("cancelled")} disabled={saving} className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-cream text-ink-soft border border-sand hover:border-red-300 hover:text-red-700 px-4 py-2.5 text-xs font-medium transition-colors">
              <Trash2 size={12} /> {isBlock ? "Remove block" : "Cancel"}
            </button>
          )}
          {booking.series_id && booking.status !== "cancelled" && (
            <button
              type="button"
              onClick={cancelSeries}
              disabled={saving}
              data-testid="cancel-series-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-cream text-ink-soft border border-sand hover:border-red-300 hover:text-red-700 px-4 py-2.5 text-xs font-medium transition-colors"
            >
              <Trash2 size={12} /> Cancel this + future
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
